import { Redirect, useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { ActivityIndicator, View } from 'react-native';

import { useSession } from '@/components/session-provider';
import { useTenant } from '@/components/tenant-provider';

/**
 * Gates the admin `[tenant]` area: resolves the active tenant from the URL and
 * enforces tenant-admin (or global-admin) access. Shared by the native and web
 * admin layouts so the access rules can't drift between platforms.
 */
export function AdminTenantGuard({ children }: Readonly<{ children: React.ReactNode }>) {
  const { tenant } = useLocalSearchParams<{ tenant: string }>();
  const { isAdmin, isProfileLoading } = useSession();
  const { tenants, activeTenant, isTenantAdmin, isTenantLoading, setActiveTenantSlug } =
    useTenant();

  React.useEffect(() => {
    setActiveTenantSlug(tenant ?? null);
  }, [tenant, setActiveTenantSlug]);

  const loading = isProfileLoading || isTenantLoading;
  const tenantExists = tenants.some((item) => item.slug === tenant);

  if (loading || (tenantExists && activeTenant?.slug !== tenant)) {
    return (
      <View className="flex-1 items-center justify-center bg-background">
        <ActivityIndicator />
      </View>
    );
  }

  if (!tenantExists) {
    return <Redirect href="/(authenticated)/admin" />;
  }

  if (!isAdmin && !isTenantAdmin) {
    return <Redirect href="/app" />;
  }

  return <>{children}</>;
}
